/**
 * TheNewsPulse — admin moderation queue for community submissions.
 * Route: /admin/contributions  (editor + admin only, backend-gated).
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { npApi } from "./api";
import { CheckCircle2, XCircle, Loader2, Clock, Filter, ExternalLink } from "lucide-react";

const TABS = [
  { key: "pending_review", label: "Pending" },
  { key: "approved",       label: "Approved" },
  { key: "rejected",       label: "Rejected" },
];

export default function NewsPulseAdminContributions() {
  const { user, loading: authLoading } = useAuth() || {};
  const [tab, setTab] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const canSee = user && (user.is_admin || user.platform_role === "editor" || user.platform_role === "super_admin");

  const refresh = async () => {
    setErr(null);
    try {
      const d = await npApi.contribQueue(tab);
      setRows(d.submissions || []);
      setCounts(d.counts || {});
    } catch (e) { setErr(e.message); }
  };

  useEffect(() => { if (canSee) refresh(); /* eslint-disable-next-line */ }, [tab, canSee]);

  if (authLoading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!canSee) {
    return (
      <div className="p-16 text-center text-slate-500" data-testid="admin-contributions-403">
        This page is restricted to editors and admins.
      </div>
    );
  }

  async function onApprove(id) {
    setBusy(id); setErr(null);
    try { await npApi.contribApprove(id); await refresh(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  }
  async function onReject() {
    if (!rejectFor || rejectReason.trim().length < 3) return;
    setBusy(rejectFor); setErr(null);
    try {
      await npApi.contribReject(rejectFor, rejectReason.trim());
      setRejectFor(null); setRejectReason("");
      await refresh();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 lg:px-6 py-10" data-testid="admin-contributions-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-1.5">
          Editorial · Community submissions
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
            style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
          Community Queue
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    tab === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`admin-contribute-tab-${t.key}`}>
            {t.label} <span className="text-[11px] text-slate-500">({counts[t.key] ?? 0})</span>
          </button>
        ))}
        <button onClick={refresh} className="ml-auto text-[12px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="admin-contribute-refresh">
          <Filter className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900 mb-4">{err}</div>}

      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-10 text-center text-slate-500"
             data-testid="admin-contributions-empty">
          Nothing here right now.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-contributions-list">
          {rows.map((r) => {
            const loc = r.location || {};
            const locStr = [loc.neighborhood, loc.city, loc.state, loc.country].filter(Boolean).join(", ");
            const isBusy = busy === r.id;
            return (
              <li key={r.id}
                  className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-200 transition-colors"
                  data-testid={`admin-contribution-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-base font-bold text-slate-900 flex-1">{r.headline}</h3>
                  <span className="text-[11px] text-slate-500 shrink-0">
                    {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  </span>
                </div>
                <div className="text-[12px] text-slate-500 mb-2">
                  {locStr || "Location TBD"} · <strong>{r.category}</strong> · by {r.author_display_name || r.author_email || r.author_id}
                  {r.firsthand ? " · firsthand" : " · second-hand"}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-line line-clamp-6">{r.story}</p>

                {(r.photo_urls || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {r.photo_urls.map((u, i) => (
                      <img key={u + i} src={u} alt="" className="w-20 h-20 rounded object-cover border border-slate-200" />
                    ))}
                  </div>
                )}

                {(r.source_links || []).length > 0 && (
                  <ul className="mt-3 text-[12px] space-y-1 text-slate-600">
                    {r.source_links.map((s) => (
                      <li key={s}><a href={s} target="_blank" rel="noreferrer noopener"
                                     className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
                        {s} <ExternalLink className="w-3 h-3" />
                      </a></li>
                    ))}
                  </ul>
                )}

                {r.status === "rejected" && r.reject_reason && (
                  <div className="mt-3 text-[12px] rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                    <strong>Rejection:</strong> {r.reject_reason}
                  </div>
                )}
                {r.status === "approved" && r.published_event_id && (
                  <div className="mt-3 text-[12px] rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-green-900">
                    <strong>Published:</strong> <a href={`/event/${r.published_event_id}`} className="underline">
                      {r.published_event_id}
                    </a>
                  </div>
                )}

                {/* Actions */}
                {r.status === "pending_review" && (
                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                    <button onClick={() => onApprove(r.id)} disabled={isBusy}
                            className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors disabled:bg-slate-300"
                            data-testid={`admin-contribute-approve-${r.id}`}>
                      {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      Approve & publish
                    </button>
                    <button onClick={() => { setRejectFor(r.id); setRejectReason(""); }} disabled={isBusy}
                            className="inline-flex items-center gap-1 border border-slate-300 hover:border-rose-400 hover:text-rose-700 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors"
                            data-testid={`admin-contribute-reject-${r.id}`}>
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {rejectFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-reject-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-base font-bold mb-2">Reject submission</h3>
            <p className="text-sm text-slate-600 mb-3">
              Tell the reporter why — this is shown on their My Contributions page.
            </p>
            <textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                      className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none"
                      placeholder="e.g. Needs a source link; the incident isn't independently verifiable yet."
                      data-testid="admin-reject-reason" />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setRejectFor(null)}
                      className="text-sm text-slate-600 hover:text-slate-900 px-4 py-2"
                      data-testid="admin-reject-cancel">Cancel</button>
              <button onClick={onReject} disabled={rejectReason.trim().length < 3}
                      className="bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2 rounded-full"
                      data-testid="admin-reject-confirm">
                Confirm rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
